/**
 * FocusTrap
 * ---------
 * Lightweight focus trapping utility compatible with the `focus-trap`
 * package API surface. It traps keyboard navigation (Tab / Shift+Tab) inside
 * a DOM container and optionally coordinates with `OverlayManager` to show a
 * shared overlay while the trap is active.
 *
 * API
 * ---
 * - constructor(element, options)
 * - activate()
 * - deactivate()
 *
 * Options (partial)
 * - initialFocus: selector string for element inside the container to focus
 * - returnFocusOnDeactivate: boolean (default true)
 * - escapeDeactivates: boolean (default true)
 * - overlay: boolean (if true the trap will show a shared overlay)
 *
 * Integration with OverlayManager
 * -------------------------------
 * When `options.overlay` is truthy the FocusTrap will call
 * `OverlayManager.show()` and capture the returned owner token.
 * On deactivate it will call `OverlayManager.hide(token)` to release exactly
 * the owner it created. The FocusTrap also registers a click handler via
 * `OverlayManager.add()` that will deactivate the trap when the user clicks
 * outside the container.
 *
 * Notes & migration
 * -----------------
 * - FocusTrap swallows overlay errors to be resilient when OverlayManager
 *   is not available; consider exposing warnings during development.
 * - initialFocus can be extended to accept an element or a function; keep
 *   tests in mind if changing the behavior.
 */

import OverlayManager from '@stardust/overlay-manager';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'input:not([disabled]):not([type="hidden"]):not([aria-hidden])',
  'select:not([disabled]):not([aria-hidden])',
  'textarea:not([disabled]):not([aria-hidden])',
  'button:not([disabled]):not([aria-hidden])',
  'details > summary',
  'iframe',
  'object',
  'embed',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]:not([tabindex^="-"])'
].join(',');

export class FocusTrap {
  constructor(element, options = {}) {
    this.element = element;
    this.options = {
      initialFocus: options.initialFocus,
      returnFocusOnDeactivate: options.returnFocusOnDeactivate !== false,
      escapeDeactivates: options.escapeDeactivates !== false,
      ...options
    };

    this.active = false;
    this.previouslyFocused = null;
    this._handleKeydown = this._handleKeydown.bind(this);
    this._handleFocusin = this._handleFocusin.bind(this);
    this._overlayClickHandler = this._overlayClickHandler.bind(this);
    this._overlayOwnerToken = null;
    this._redirectingFocus = false;
    this._initialFocusFrame = null;
    this._returnFocusFrame = null;
  }

  _overlayClickHandler(e) {
    // If click happened inside the trap element, ignore
    if (this.element && this.element.contains(e.target)) return;
    // Otherwise deactivate trap
    this.deactivate();
  }

  /**
   * Get all focusable elements within the trap container
   */
  _getFocusableElements() {
    const elements = this.element.querySelectorAll(FOCUSABLE_SELECTOR);

    return Array.from(elements).filter(el => {
      const closedDetails = el.closest('details:not([open])');
      const visibleSummary = el.closest('summary');

      return (!closedDetails || visibleSummary?.parentElement === closedDetails) &&
        !el.closest('[inert]') &&
        !el.matches(':disabled') &&
        // Negative tabindex = removed from the tab order (e.g. redundant
        // icon-submit buttons); the trap's wrap-around must skip it too.
        !el.matches('[tabindex^="-"]') &&
        el.getClientRects().length > 0 &&
        getComputedStyle(el).visibility !== 'hidden';
    });
  }

  /**
   * Focus an element without scrolling. Returns whether focus moved successfully.
   * @param {Element|null|undefined} element
   * @returns {boolean}
   */
  _focus(element) {
    if (
      !(element instanceof Element) ||
      typeof element.focus !== 'function' ||
      !element.isConnected
    ) {
      return false;
    }

    try {
      element.focus({ preventScroll: true });
      return document.activeElement === element;
    } catch (error) {
      return false;
    }
  }

  /**
   * Recover focus if another handler or a DOM update moves it outside an active trap.
   */
  _handleFocusin(e) {
    if (
      !this.active ||
      this._redirectingFocus ||
      this.element.contains(e.target)
    ) {
      return;
    }

    const target = this._getFocusableElements()[0] || this.element;
    this._redirectingFocus = true;
    try {
      this._focus(target);
    } finally {
      this._redirectingFocus = false;
    }
  }

  /**
   * Handle tab key to trap focus
   */
  _handleKeydown(e) {
    if (!this.active) return;

    // Handle Escape key
    if (e.key === 'Escape' && this.options.escapeDeactivates) {
      e.preventDefault();
      this.deactivate();
      return;
    }

    // Handle Tab key
    if (e.key === 'Tab') {
      const focusableElements = this._getFocusableElements();

      if (focusableElements.length === 0) {
        e.preventDefault();
        this._focus(this.element);
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;
      const focusIsInside = this.element.contains(activeElement);

      if (e.shiftKey) {
        // Shift + Tab
        if (!focusIsInside || activeElement === firstElement) {
          e.preventDefault();
          this._focus(lastElement);
        }
      } else {
        // Tab
        if (!focusIsInside || activeElement === lastElement) {
          e.preventDefault();
          this._focus(firstElement);
        }
      }
    }
  }

  /**
   * Activate the focus trap
   */
  activate() {
    if (this.active) return;

    if (this._returnFocusFrame !== null) {
      cancelAnimationFrame(this._returnFocusFrame);
      this._returnFocusFrame = null;
    }

    this.active = true;
    this.previouslyFocused = document.activeElement;

    // Add keyboard listener
    document.addEventListener('keydown', this._handleKeydown, { capture: true });
    document.addEventListener('focusin', this._handleFocusin, { capture: true });

    // Defer the focusable-element scan to rAF — querySelectorAll + visibility reads
    // force synchronous layout for every element found, which tanks INP.
    this._initialFocusFrame = requestAnimationFrame(() => {
      this._initialFocusFrame = null;
      if (!this.active) return; // deactivated before frame fired
      const focusableElements = this._getFocusableElements();
      const initialFocus = this.options.initialFocus
        ? this.element.querySelector(this.options.initialFocus)
        : null;
      if (!this._focus(initialFocus)) {
        this._focus(focusableElements[0] || this.element);
      }
    });

    // If overlay support requested, register overlay click handler and show overlay
    if (this.options.overlay) {
      try {
        // Show the shared overlay and increment its ownership count.
        try {
          // Capture ownership token so we can reliably release only what
          // we created.
          this._overlayOwnerToken = null;
          try { this._overlayOwnerToken = OverlayManager.show(); } catch (e) { /* ignore show error */ }
        } catch (e) { }
        OverlayManager.add(this._overlayClickHandler);
      } catch (e) { }
    }
  }

  /**
   * Deactivate the focus trap
   */
  deactivate() {
    if (!this.active) return;

    this.active = false;

    if (this._initialFocusFrame !== null) {
      cancelAnimationFrame(this._initialFocusFrame);
      this._initialFocusFrame = null;
    }

    // Remove keyboard listener
    document.removeEventListener('keydown', this._handleKeydown, { capture: true });
    document.removeEventListener('focusin', this._handleFocusin, { capture: true });

    // Remove overlay registration
    if (this.options.overlay) {
      try {
        OverlayManager.remove(this._overlayClickHandler);
      } catch (e) { }

      // If we successfully received an owner token when showing, release that specific owner.
      try {
        if (this._overlayOwnerToken) {
          OverlayManager.hide(this._overlayOwnerToken);
        } else {
          // fallback to legacy hide() behavior
          OverlayManager.hide();
        }
      } catch (e) { }
      this._overlayOwnerToken = null;
    }

    // Return focus to previously focused element
    if (this.options.returnFocusOnDeactivate && this.previouslyFocused) {
      // Use rAF to avoid blocking INP, but focus immediately after current frame
      const returnTarget = this.previouslyFocused;
      this._returnFocusFrame = requestAnimationFrame(() => {
        this._returnFocusFrame = null;
        this._focus(returnTarget);
      });
    }
  }
}

/**
 * Create a focus trap instance (API compatible with focus-trap package)
 */
export function createFocusTrap(element, options) {
  return new FocusTrap(element, options);
}

export default { createFocusTrap, FocusTrap };
